/**
 * Layout computation for the 3 constellation directions.
 *
 * Pure functions: input = the TwinNode list; output = positions
 * per node ID in a 1240 x 720 viewBox. Mirrors the prototype's
 * twin-data.js layouts but computed at render time off live data
 * (the prototype hand-placed the 22 demo nodes; we compute the
 * Flow + Orbit layouts deterministically + the Observatory layout
 * with a seeded scatter so the same data lands in the same spots
 * across renders).
 */
import type { TwinEdge, TwinNode, TwinNodeKind } from "shared/api/types";
import { laneLayout } from "./spacing";

export const VIEW_W = 1240;
export const VIEW_H = 720;

export interface NodePos {
  x: number;
  y: number;
}

export interface LayoutResult {
  positions: Record<string, NodePos>;
  /** Orbit layout only: ring centre + radii per kind + the
   * horizontal stretch the rings + node x use (so TwinViz draws
   * the ellipse rings at the same width as the nodes). */
  centre?: NodePos;
  radii?: Record<TwinNodeKind, number>;
  xStretch?: number;
  /** Service-ring layout only: the single perimeter + its membership
   * split, so the renderer can draw the rim and separate the
   * "no modelled dependencies" arc. */
  ring?: RingInfo;
}

export interface RingInfo {
  centre: NodePos;
  radius: number;
  /** Ids on the main arc -- services in >=1 dependency edge, ordered so
   * cluster members sit adjacent and chords stay short. */
  connectedIds: string[];
  /** Ids on the trailing arc -- services with no modelled dependency.
   * Empty unless `includeIsolated` was requested. */
  isolatedIds: string[];
}

const KIND_ORDER: TwinNodeKind[] = ["channel", "ibs", "service", "vendor"];

/**
 * Flow column x-positions, scaled to the live canvas width so the
 * columns fan across the full stage instead of clustering left in
 * a fixed 1240-wide box. At the design width (VIEW_W) the columns
 * land on their original 150 / 470 / 800 / 1110 marks. Shared by
 * flowLayout + TwinViz's column headings so they stay aligned.
 */
export function flowColumns(viewW: number = VIEW_W): Record<TwinNodeKind, number> {
  const s = viewW / VIEW_W;
  return {
    channel: 150 * s,
    ibs: 470 * s,
    service: 800 * s,
    vendor: 1110 * s,
  };
}

/**
 * Dependency Flow -- columns by kind, left to right.
 * Channels (customer-facing) on the left; vendors (suppliers) on
 * the right. This is the v1 blast-radius column SVG evolved.
 */
export function flowLayout(nodes: TwinNode[], viewW: number = VIEW_W): LayoutResult {
  const cols = flowColumns(viewW);
  const buckets: Record<TwinNodeKind, string[]> = {
    channel: [],
    ibs: [],
    service: [],
    vendor: [],
  };
  for (const n of nodes) buckets[n.kind].push(n.id);

  const top = 90;
  const bot = VIEW_H - 70;
  const positions: Record<string, NodePos> = {};

  // P404: a column that cannot hold its nodes at a legible spacing
  // gains lanes. Measured before changing anything: 78 services down a
  // 560px band is 7.3px apart, against an orb 6-8px across -- they
  // physically overlapped. `laneLayout` returns a single lane unchanged
  // when the band already fits, so smaller estates and the existing
  // column contract are untouched.
  for (const k of KIND_ORDER) {
    const { positions: lanes } = laneLayout(buckets[k], { top, bottom: bot });
    for (const [id, pos] of Object.entries(lanes)) {
      positions[id] = { x: cols[k] + pos.dx, y: pos.y };
    }
  }

  return { positions };
}

/**
 * Resilience Orbit -- concentric rings, vendor core -> channel rim.
 * Position around each ring by index/count; perspective stretch via
 * 1.32 x 0.92 aspect so the ellipse hints at a 3D ring without
 * needing a real transform.
 */
export function orbitLayout(nodes: TwinNode[], viewW: number = VIEW_W): LayoutResult {
  const cx = viewW / 2;
  const cy = VIEW_H / 2 + 6;
  // Stretch the rings horizontally with the canvas so the orbit
  // fills a wide stage instead of sitting in a narrow centre column.
  // At the design width this is the original 1.32.
  const xStretch = 1.32 * (viewW / VIEW_W);
  const radii: Record<TwinNodeKind, number> = {
    vendor: 92,
    service: 196,
    ibs: 300,
    channel: 392,
  };

  const buckets: Record<TwinNodeKind, string[]> = {
    channel: [],
    ibs: [],
    service: [],
    vendor: [],
  };
  for (const n of nodes) buckets[n.kind].push(n.id);

  const positions: Record<string, NodePos> = {};
  for (const k of KIND_ORDER) {
    const ids = buckets[k];
    const r = radii[k];
    if (ids.length === 0) continue;
    ids.forEach((id, i) => {
      const a = ((-90 + (360 * i) / ids.length) * Math.PI) / 180;
      positions[id] = {
        x: cx + r * Math.cos(a) * xStretch,
        y: cy + r * Math.sin(a) * 0.92,
      };
    });
  }
  return { positions, centre: { x: cx, y: cy }, radii, xStretch };
}

/**
 * Observatory -- organic night-sky scatter. Positions derived from
 * an FNV-1a hash of the node id so a re-render lands the same node
 * in the same spot. Constrained to a tier band by kind (channels
 * at the top, vendors at the bottom) so the scatter still reads
 * as a constellation, not a starburst.
 */
export function observatoryLayout(
  nodes: TwinNode[],
  viewW: number = VIEW_W,
  /**
   * Round 11 C1 -- canvas the scene must not draw in.
   *
   * The application's own floating queue panel is pinned over the
   * bottom-start corner of the stage, and it ate two vendors: a real
   * mouse click at "AWS -- Riyadh region" hit the panel, and the node
   * beneath it could not be selected at all. The panel is not the
   * scene's to move, so the scene reserves for it.
   *
   * Width in VIEWBOX units, measured by the caller from the panel's
   * own rect through the same scale it already computes -- zero when
   * the panel is collapsed or absent, so nothing moves on a page that
   * does not have one.
   */
  reserveBottomStart = 0,
  /**
   * ... and the other corner, where the inspector opens.
   *
   * Round 11 gave this a default of zero and a reason: the inspector
   * carries a close button, so a node beneath it is one click from
   * reachable, and reserving would cost a third of the bottom row.
   * Round 12 measured what that costs instead -- with the inspector
   * open, 13 of 78 nodes select the wrong entity and three of those
   * press one of the inspector's OWN controls on the previously
   * selected node. The caller passes a real reserve now, and the
   * reasoning is kept in `ConstellationView` where the decision lives.
   */
  reserveBottomEnd = 0,
): LayoutResult {
  // Tier bands: per-kind [minY, maxY] in the viewBox.
  const bands: Record<TwinNodeKind, [number, number]> = {
    channel: [50, 140],
    ibs: [180, 320],
    service: [350, 560],
    vendor: [600, 690],
  };
  const minX = 80;
  const maxX = viewW - 80;
  const spanX = maxX - minX;

  // Bucket by kind so x spreads EVENLY across the row by index.
  // A pure hash for x (the original) clustered orbs into clumps;
  // distributing by index guarantees separation while a bounded
  // hash jitter (x + y) keeps the organic night-sky feel rather
  // than a rigid grid. Sort ids so the index order is stable
  // across renders (deterministic).
  const buckets: Record<TwinNodeKind, string[]> = {
    channel: [],
    ibs: [],
    service: [],
    vendor: [],
  };
  for (const n of nodes) buckets[n.kind].push(n.id);

  const positions: Record<string, NodePos> = {};
  for (const k of KIND_ORDER) {
    const ids = buckets[k].slice().sort();
    const [yMin, yMax] = bands[k];
    const bandH = yMax - yMin;
    /* The reserve applies to the BOTTOM band only, which is the one
       the panel overlaps. Capped at half the row so a wide panel
       narrows the vendors rather than stacking them: a scene that
       reserves itself out of existence is not an improvement. */
    const isBottom = k === "vendor";
    const capped = (v: number) => Math.max(0, Math.min(v, spanX / 3));
    const reserve = isBottom ? capped(reserveBottomStart) : 0;
    /* Round 12 E4 -- the END reserve covers TWO bands, and reserving
       only the vendor row was measured to leave six service nodes still
       under the panel. The inspector is 289px tall on a 600px stage, so
       in viewBox units it starts around y=364: the top of the service
       band (350..560), not the bottom of it. The queue panel at the
       other corner is short enough that the vendor row alone is right
       for it, which is why the two reserves do not share this rule. */
    const isUnderInspector = k === "vendor" || k === "service";
    const reserveEnd = isUnderInspector ? capped(reserveBottomEnd) : 0;
    const rowMinX = minX + reserve;
    const rowMaxX = maxX - reserveEnd;
    const rowSpanX = Math.max(80, rowMaxX - rowMinX);
    const slot = ids.length > 0 ? rowSpanX / ids.length : rowSpanX;
    // Jitter capped to 35% of a slot so neighbours never swap /
    // collide (worst-case adjacent gap stays at 0.3 x slot), but
    // the row doesn't read as evenly-pitched.
    const jx = Math.min(slot * 0.35, 40);
    ids.forEach((id, i) => {
      const hx = fnv1a(id + "::x");
      const hy = fnv1a(id + "::y");
      const baseX = rowMinX + slot * (i + 0.5);
      const offX = ((hx % 1000) / 1000 - 0.5) * 2 * jx;
      positions[id] = {
        x: Math.max(rowMinX, Math.min(rowMaxX, baseX + offX)),
        y: yMin + (hy % 1000) * (bandH / 1000),
      };
    });
  }
  return { positions };
}

function fnv1a(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// ── P385: service dependency ring ───────────────────────────────────

/** A service -> service dependency edge, either source (junction or
 * hierarchy). Both ends must be services; other kinds are not on the
 * ring. */
function isServiceDependency(e: TwinEdge): boolean {
  return (
    e.from_kind === "service" &&
    e.to_kind === "service" &&
    e.relation === "depends_on" &&
    e.from_id !== e.to_id
  );
}

/**
 * Group connected services into clusters over the UNDIRECTED dependency
 * subgraph, so ring neighbours are graph neighbours and chords stay
 * short. Returns clusters ordered largest-first, each member-sorted;
 * ties break on the smallest member id so the ring is deterministic
 * across renders (the observatoryLayout precedent).
 */
export function clusterServices(serviceIds: string[], edges: TwinEdge[]): string[][] {
  const inRing = new Set(serviceIds);
  const adj = new Map<string, Set<string>>();
  for (const id of serviceIds) adj.set(id, new Set());
  for (const e of edges) {
    if (!isServiceDependency(e)) continue;
    if (!inRing.has(e.from_id) || !inRing.has(e.to_id)) continue;
    adj.get(e.from_id)!.add(e.to_id);
    adj.get(e.to_id)!.add(e.from_id);
  }

  const seen = new Set<string>();
  const clusters: string[][] = [];
  // Walk seeds in sorted order so component discovery is deterministic.
  for (const seed of [...serviceIds].sort()) {
    if (seen.has(seed)) continue;
    const members: string[] = [];
    const stack = [seed];
    seen.add(seed);
    // Cycle-safe: `seen` gates enqueue, so the real estate cycle
    // (AuthHost + T24 + PaymentsHub + mada) is walked once.
    while (stack.length > 0) {
      const cur = stack.pop()!;
      members.push(cur);
      for (const nxt of [...(adj.get(cur) ?? [])].sort()) {
        if (seen.has(nxt)) continue;
        seen.add(nxt);
        stack.push(nxt);
      }
    }
    clusters.push(members.sort());
  }

  clusters.sort((a, b) => {
    if (b.length !== a.length) return b.length - a.length;
    return a[0]!.localeCompare(b[0]!);
  });
  return clusters;
}

export interface ServiceRingOptions {
  /** Admit services with no modelled dependency onto a trailing arc.
   * Off by default: on the live estate only 37 of 78 services carry a
   * dependency edge, so a full ring is mostly empty of meaning. */
  includeIsolated?: boolean;
}

/**
 * Service dependency ring -- every service on ONE perimeter, with
 * dependencies drawn as chords across the interior.
 *
 * The concentric orbitLayout already puts services on a single ring,
 * but sandwiched between vendors and IBSes with radial edges, so
 * same-kind edges have nowhere to go. This layout exists for the
 * service -> service graph specifically: the one the P366 recovery
 * sequencer schedules on, and which no view renders today.
 *
 * Isolated services sit on their own arc behind a visible gap rather
 * than being silently dropped -- "no modelled dependencies" is a fact
 * about the estate's CMDB, not a rendering gap, and should read as one.
 */
export function serviceRingLayout(
  nodes: TwinNode[],
  edges: TwinEdge[],
  viewW: number = VIEW_W,
  opts: ServiceRingOptions = {},
): LayoutResult {
  const cx = viewW / 2;
  const cy = VIEW_H / 2;
  const radius = Math.min(VIEW_H, viewW) / 2 - 90;

  const serviceIds = nodes.filter((n) => n.kind === "service").map((n) => n.id);
  const connectedSet = new Set<string>();
  for (const e of edges) {
    if (!isServiceDependency(e)) continue;
    connectedSet.add(e.from_id);
    connectedSet.add(e.to_id);
  }

  const connectedIds = serviceIds.filter((id) => connectedSet.has(id));
  const isolatedIds = opts.includeIsolated
    ? serviceIds.filter((id) => !connectedSet.has(id)).sort()
    : [];

  const ordered = clusterServices(connectedIds, edges).flat();
  const positions: Record<string, NodePos> = {};

  // Gap (in slots) separating the connected arc from the isolated one,
  // so the two groups never read as one continuous ring.
  const GAP_SLOTS = 2;
  const total =
    ordered.length + isolatedIds.length + (isolatedIds.length > 0 ? GAP_SLOTS * 2 : 0);

  const place = (id: string, slot: number): void => {
    const a = ((-90 + (360 * slot) / Math.max(total, 1)) * Math.PI) / 180;
    positions[id] = { x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) };
  };

  ordered.forEach((id, i) => place(id, i));
  isolatedIds.forEach((id, i) => place(id, ordered.length + GAP_SLOTS + i));

  return {
    positions,
    centre: { x: cx, y: cy },
    ring: { centre: { x: cx, y: cy }, radius, connectedIds: ordered, isolatedIds },
  };
}
